import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { QueuedJobRow } from "../components/QueuedJobRow.js";
import { OpsHistoryShell } from "../components/OpsHistoryShell.js";

/**
 * The whole live queue, filterable by kind, state and subject: is anything queued for a
 * company, why is a retry not running, what a backfill consists of.
 *
 * **No "load more".** The queue drains from the front while it is read, so page two of a
 * cursor walk would repeat and skip rows with no way for the reader to tell. One bounded
 * page in claim order, with the total beside it, is true at the instant it was read, and
 * the filters are what narrow it.
 *
 * **No composite health number**: it would become the thing that is read instead of the
 * rows.
 */
export function QueuePage() {
  /** The state filter is seeded from the URL, so a link can land on the parked half. */
  const [params] = useSearchParams();
  const [kind, setKind] = useState("");
  const [state, setState] = useState<"" | "pending" | "failed">(() => {
    const seed = params.get("state");
    return seed === "pending" || seed === "failed" ? seed : "";
  });
  /**
   * What is typed and what has been asked for are separate, so a search runs on submit
   * rather than on every keystroke.
   */
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const kinds = useQuery({ queryKey: ["ops", "queue", "kinds"], queryFn: api.opsQueueKinds });

  /** Refetched on a cadence rather than once, because the queue drains while it is on screen. */
  const queue = useQuery({
    queryKey: ["ops", "queue", { kind, state, q }],
    queryFn: () =>
      api.opsQueue({
        ...(kind ? { kind } : {}),
        ...(state ? { state } : {}),
        ...(q ? { q } : {}),
      }),
    refetchInterval: 30_000,
  });

  const items = queue.data?.items ?? [];
  const total = queue.data?.total ?? null;
  const filtered = Boolean(kind || state || q);

  return (
    <OpsHistoryShell
      title="The queue"
      deck="Everything waiting to run and everything parked, in the order a worker would claim it. A successful job leaves here for the history; nothing else is ever dropped."
      count={
        total === null
          ? `${items.length} shown`
          : `${items.length} of ${total.toLocaleString()} live job${total === 1 ? "" : "s"}`
      }
      note={
        <>
          {filtered && " — filtered"}
          {/*
            A cap that was reached is said: a page that stops at its limit otherwise reads
            exactly like the whole list.
          */}
          {queue.data?.truncated && " — narrow this with the filters to see the rest"}
          {queue.data?.truncatedMatches && (
            <>
              {" "}
              <span className="histtrunc">
                That term matches more companies than this search will resolve — narrow it.
              </span>
            </>
          )}
        </>
      }
      isPending={queue.isPending}
      error={queue.isError ? (queue.error as Error) : null}
      isEmpty={items.length === 0}
      emptyFiltered={filtered}
      emptyAll="Nothing is queued, running or parked. The queue is empty."
      /* See the header: a cursor walk over a draining queue repeats and skips silently. */
      onLoadMore={null}
      loadingMore={false}
      filters={
        <form
          className="histfilters"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(draft.trim());
          }}
        >
          <label>
            <span className="k">Kind</span>
            {/*
              Only the kinds with something live. Offering one with nothing queued reads as
              a stage that is stuck rather than one with nothing to do.
            */}
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">any</option>
              {(kinds.data?.kinds ?? []).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="k">State</span>
            <select
              value={state}
              onChange={(e) => setState(e.target.value as "" | "pending" | "failed")}
            >
              <option value="">any</option>
              <option value="pending">waiting</option>
              <option value="failed">parked</option>
            </select>
          </label>
          <label className="histsearch">
            <span className="k">Company, ticker, accession or situation</span>
            <input
              value={draft}
              placeholder="SNDK, SanDisk, 0001104659-25-010902…"
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button type="submit" className="iconbtn">
            Search
          </button>
          {filtered && (
            <button
              type="button"
              className="iconbtn"
              onClick={() => {
                setKind("");
                setState("");
                setDraft("");
                setQ("");
              }}
            >
              Clear
            </button>
          )}
        </form>
      }
    >
      {items.map((job) => (
        <QueuedJobRow
          key={job.id}
          job={job}
          open={openId === job.id}
          onToggle={() => setOpenId(openId === job.id ? null : job.id)}
        />
      ))}
    </OpsHistoryShell>
  );
}
